/**
 * Calendar Component - Olympic Fit Wellness
 * Uses FullCalendar for displaying sessions
 */
import React, { useState, useEffect, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { format } from 'date-fns';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Users, 
  MapPin, 
  DollarSign,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

// Session type colors
const SESSION_COLORS = {
  group_class: { bg: '#3B82F6', text: 'Group Class' },
  one_on_one: { bg: '#10B981', text: '1-on-1 Session' },
  workshop: { bg: '#8B5CF6', text: 'Workshop' }
};

export default function SessionCalendar({ staffId = null, showBooking = true }) {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingMessage, setBookingMessage] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [currentView, setCurrentView] = useState('timeGridWeek');

  // Fetch sessions
  const fetchSessions = useCallback(async (start, end) => {
    try {
      setLoading(true);
      const params = {
        start_date: start || new Date().toISOString(),
        end_date: end || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      };
      if (staffId) params.staff_id = staffId;
      
      const data = await api.getCalendarSessions(params);
      setSessions(data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Handle date range change
  const handleDatesSet = (dateInfo) => {
    fetchSessions(dateInfo.startStr, dateInfo.endStr);
  };

  // Handle event click
  const handleEventClick = (clickInfo) => {
    const session = clickInfo.event;
    setSelectedSession({
      id: session.id,
      title: session.title,
      start: session.start,
      end: session.end,
      ...session.extendedProps
    });
    if (showBooking) {
      setShowBookingModal(true);
    }
  };

  // Book session
  const handleBookSession = async () => {
    if (!selectedSession) return;
    
    setBookingLoading(true);
    try {
      const isRequest = selectedSession.session_type === 'one_on_one';
      const result = await api.createCalendarBooking(
        selectedSession.id,
        isRequest ? bookingMessage : null,
        isRequest
      );
      
      if (result.success) {
        toast.success(result.message || 'Booking created!');
        setShowBookingModal(false);
        setBookingMessage('');
        fetchSessions(); // Refresh to update participant counts
      }
    } catch (error) {
      toast.error(error.message || 'Failed to book session');
    } finally {
      setBookingLoading(false);
    }
  };

  // Custom event render
  const renderEventContent = (eventInfo) => {
    const { extendedProps } = eventInfo.event;
    const isFull = extendedProps?.is_full;
    
    return (
      <div className="p-1 overflow-hidden">
        <div className="font-medium text-xs truncate">{eventInfo.event.title}</div>
        {currentView !== 'dayGridMonth' && (
          <div className="text-xs opacity-80 truncate">
            {extendedProps?.staff_name}
            {!isFull && extendedProps?.spots_left !== undefined && (
              <span className="ml-1">• {extendedProps.spots_left} spots</span>
            )}
            {isFull && <span className="ml-1 text-red-200">• Full</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Calendar Header */}
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CalendarIcon className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold">Class Schedule</h2>
        </div>
        <div className="flex gap-2">
          {Object.entries(SESSION_COLORS).map(([type, { bg, text }]) => (
            <div key={type} className="flex items-center gap-1 text-xs">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: bg }} />
              <span className="text-gray-600">{text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div className="p-4 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        )}
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
          }}
          events={sessions}
          eventClick={handleEventClick}
          eventContent={renderEventContent}
          datesSet={handleDatesSet}
          viewDidMount={(info) => setCurrentView(info.view.type)}
          slotMinTime="06:00:00"
          slotMaxTime="21:00:00"
          allDaySlot={false}
          weekends={true}
          nowIndicator={true}
          height="auto"
          eventTimeFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
          slotLabelFormat={{
            hour: 'numeric',
            minute: '2-digit',
            meridiem: 'short'
          }}
        />
      </div>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedSession?.title}</DialogTitle>
            <DialogDescription>
              {selectedSession?.session_type && (
                <span 
                  className="inline-block px-2 py-0.5 rounded text-white text-xs mt-1"
                  style={{ backgroundColor: SESSION_COLORS[selectedSession.session_type]?.bg }}
                >
                  {SESSION_COLORS[selectedSession.session_type]?.text}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedSession && (
            <div className="space-y-4">
              {/* Session Details */}
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    {selectedSession.start && format(new Date(selectedSession.start), 'EEEE, MMMM d, yyyy')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>
                    {selectedSession.start && format(new Date(selectedSession.start), 'h:mm a')} - 
                    {selectedSession.end && format(new Date(selectedSession.end), 'h:mm a')}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{selectedSession.staff_name || 'Staff'}</span>
                </div>
                {selectedSession.session_type === 'group_class' && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Users className="w-4 h-4" />
                    <span>
                      {selectedSession.max_participants - selectedSession.current_participants} spots remaining
                      {selectedSession.current_participants >= selectedSession.max_participants && (
                        <span className="text-red-500 ml-2">(Full)</span>
                      )}
                    </span>
                  </div>
                )}
                {selectedSession.price > 0 && !selectedSession.included_in_membership && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    <span>${selectedSession.price}</span>
                  </div>
                )}
                {selectedSession.included_in_membership && (
                  <div className="flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Included in membership</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {selectedSession.description && (
                <div className="text-sm text-gray-600 border-t pt-3">
                  {selectedSession.description}
                </div>
              )}

              {/* Message for 1-on-1 */}
              {selectedSession.session_type === 'one_on_one' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Message to instructor (optional)</label>
                  <Textarea
                    placeholder="Let them know about any goals, injuries, or preferences..."
                    value={bookingMessage}
                    onChange={(e) => setBookingMessage(e.target.value)}
                    rows={3}
                  />
                  <p className="text-xs text-gray-500">
                    <AlertCircle className="w-3 h-3 inline mr-1" />
                    1-on-1 sessions require instructor approval
                  </p>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowBookingModal(false)}>
              Cancel
            </Button>
            {user ? (
              <Button 
                onClick={handleBookSession}
                disabled={bookingLoading || selectedSession?.extendedProps?.is_full}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {bookingLoading ? 'Booking...' : 
                  selectedSession?.session_type === 'one_on_one' ? 'Request Session' : 'Book Now'}
              </Button>
            ) : (
              <Button 
                onClick={() => window.location.href = '/login?redirect=/portal/schedule'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Login to Book
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
